import type { ProductDetail } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useCategoriesQuery } from '@/entities/category';
import { useInvalidateProducts } from '@/entities/product';
import { useToggleSet } from '@/shared/lib';
import { Button } from '@/shared/ui';
import { useSetCategories } from '../model/use-set-categories';
import { SectionCard } from './section-card';

interface CategorySectionProps {
  readonly productId: string;
  readonly links: ProductDetail['categories'];
  readonly onSaved: (message: string) => void;
}

/** 소분류 다중 연결 + 대표 1개 지정. 대분류는 선택 불가(서버도 거부). */
export const CategorySection = ({
  productId,
  links,
  onSaved,
}: CategorySectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const categoriesQuery = useCategoriesQuery();
  const saveAction = useSetCategories({
    onSuccess: () => {
      onSaved('카테고리를 저장했습니다.');
      invalidate.detail(productId);
    },
  });

  const { selected, toggle } = useToggleSet(links.map((link) => link.categoryId));
  const [primaryId, setPrimaryId] = useState<string | null>(
    () => links.find((link) => link.isPrimary)?.categoryId ?? links[0]?.categoryId ?? null,
  );

  const effectivePrimary = primaryId && selected.has(primaryId) ? primaryId : null;
  const canSave = selected.size > 0 && effectivePrimary !== null;

  return (
    <SectionCard
      title="카테고리"
      description="소분류를 1개 이상 연결하고 대표 카테고리(브레드크럼)를 지정하세요."
      error={saveAction.error?.message ?? null}
    >
      {categoriesQuery.isPending ? (
        <p className="text-muted-foreground">불러오는 중…</p>
      ) : categoriesQuery.isError ? (
        <p role="alert" className="text-destructive">
          카테고리를 불러오지 못했습니다.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {categoriesQuery.data.map((root) => (
            <fieldset key={root.id}>
              <legend className="text-sm font-medium">{root.name}</legend>
              <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1 pl-2">
                {root.children.map((leaf) => (
                  <span key={leaf.id} className="flex items-center gap-2 text-sm">
                    <label className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={selected.has(leaf.id)}
                        onChange={() => toggle(leaf.id)}
                      />
                      {leaf.name}
                    </label>
                    {selected.has(leaf.id) ? (
                      <label className="flex items-center gap-1 text-xs text-muted-foreground">
                        <input
                          type="radio"
                          name="primary-category"
                          checked={effectivePrimary === leaf.id}
                          onChange={() => setPrimaryId(leaf.id)}
                        />
                        대표
                      </label>
                    ) : null}
                  </span>
                ))}
              </div>
            </fieldset>
          ))}

          <div>
            <Button
              size="sm"
              disabled={!canSave}
              isLoading={saveAction.isPending}
              onClick={() => {
                if (effectivePrimary) {
                  saveAction.setCategories({
                    id: productId,
                    categoryIds: [...selected],
                    primaryCategoryId: effectivePrimary,
                  });
                }
              }}
            >
              카테고리 저장
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};
